import CommunicationHandler from '@mirage-projects/communication-library';
import { ProcessData } from '@mirage-projects/cw-models';

import { Loader } from './core/Loader';
import { logService } from './core/Log';
import { RunnerData } from './core/MocksTypes';
import { Runner } from './core/Runner';
import { processMocks } from './processMocks';
import { workflowMocks } from './workflowMocks';

/**
 * Loads process and workflow mocks
 */
export class PointCloudVizExternalMocks {
    private processMap = new Map<string, ProcessData>();

    constructor() {
        this.loadProcessMap();
    }

    run(): void {
        this.disablesExternalCommunication();

        const communicationHandler = new CommunicationHandler();
        logService.info('MOCKS: init comm handler', communicationHandler.id);

        this.loadWorkflows(communicationHandler);
    }

    private loadProcessMap(): void {
        this.processMap.clear();

        processMocks.forEach((processMock: ProcessData) => this.processMap.set(processMock.name, processMock));
    }

    private async loadWorkflows(communicationHandler: CommunicationHandler): Promise<void> {
        if (!communicationHandler) {
            throw new Error('Missing communicationHandler');
        }
        workflowMocks.forEach((workflow) => {
            workflow.processes.forEach(async (processRunDef) => {
                processRunDef.params = { ...(workflow.params ?? {}), ...(processRunDef.params ?? {}) };

                const processRun = this.loadProcessRun(processRunDef);

                // Block the execution until this process finishes
                if (processRunDef.blockUntilFinish) {
                    await processRun.run(communicationHandler);
                } else {
                    processRun.run(communicationHandler);
                }
            });
        });
    }

    private loadProcessRun(runDef: RunnerData): Runner {
        const processDef = typeof runDef.process === 'string' ? this.processMap.get(runDef.process) : runDef.process;

        if (!processDef) {
            throw new Error(`Process with name ${runDef.process} not found`);
        }

        const process = Loader.loadProcess(processDef);

        if (!process) {
            throw new Error(`Process couldn't be loaded`);
        }

        return new Runner({
            process,
            startCondition: runDef.startCondition,
            finishCondition: runDef.finishCondition,
            params: runDef.params,
        });
    }

    private disablesExternalCommunication(): void {
        /* Avoid crashes with UWP (C#) disabled communication */
        window.external.notify = (): void => {
            return;
        };
    }
}
